import { defineStore } from 'pinia'
import { DeepReadonly, readonly, ref } from 'vue'

import api from '@/client/core/api'
import { TrackingService } from '@/client/services'
import { useSystemStore } from '@/client/store/useSystemStore'
import { TCampaign } from '@/typings/api/campaigns.type'
import {
  TApplicant,
  TEntryCount,
  TGetApplicantsParams,
  TPutCandidateParams,
  TPutFollowerParams,
  TPutResponderParams,
  TPutWinnerContactParams,
  TPutWinnerParams
} from '@/typings/api/tiktok_applicants.type'
import { TPrize } from '@/typings/api/tiktok_campaign_prizes.type'

export type ApplicantState = {
  firstLoaded: boolean
  error: boolean
  total: number
  applicants: TApplicant[]
  params: TGetApplicantsParams
  campaign: TCampaign | null
  campaignPrize: TPrize | null
  showAdvanceFilter: boolean
  isLoading: boolean
  entryCounts: number
}

const createInitialState = (): ApplicantState => ({
  firstLoaded: false,
  error: false,
  campaign: null,
  campaignPrize: null,
  applicants: [],
  total: 0,
  params: {
    campaignId: 0,
    prizeId: 0,
    sortBy: '-publishDatetime',
    pagingNo: 1,
    limitCount: 25,
    isFollowing: '',
    isWinner: '',
    wasWinner: -1,
    isResponder: '',
    isWinnerContact: '',
    isCandidate: '',
    totalCampaignEntryCount: ['', ''],
    totalCampaignWinningCount: ['', ''],
    postsCount: ['', ''],
    accountName: '',
    forCsv: 0,
    entryDateFrom: '',
    entryDateTo: '',
    followersCount: [],
    isWidget: -1
  },
  showAdvanceFilter: false,
  isLoading: false,
  entryCounts: 0
})

export const useTikTokApplicantStore = defineStore('tiktokApplicantStore', () => {
  const systemStore = useSystemStore()
  const initialState = createInitialState()

  const firstLoaded = ref<ApplicantState['firstLoaded']>(initialState.firstLoaded)
  const error = ref<ApplicantState['error']>(initialState.error)
  const total = ref<ApplicantState['total']>(initialState.total)
  const applicants = ref<ApplicantState['applicants']>(initialState.applicants)
  const params = ref<ApplicantState['params']>(initialState.params)
  const campaign = ref<ApplicantState['campaign']>(initialState.campaign)
  const campaignPrize = ref<ApplicantState['campaignPrize']>(initialState.campaignPrize)
  const showAdvanceFilter = ref<ApplicantState['showAdvanceFilter']>(initialState.showAdvanceFilter)
  const isLoading = ref<ApplicantState['isLoading']>(initialState.isLoading)
  const entryCounts = ref<ApplicantState['entryCounts']>(initialState.entryCounts)

  const reset = async (): Promise<void> => {
    const nextState = createInitialState()
    firstLoaded.value = nextState.firstLoaded
    error.value = nextState.error
    total.value = nextState.total
    applicants.value = nextState.applicants
    params.value = nextState.params
    campaign.value = nextState.campaign
    campaignPrize.value = nextState.campaignPrize
    showAdvanceFilter.value = nextState.showAdvanceFilter
    isLoading.value = nextState.isLoading
    entryCounts.value = nextState.entryCounts
  }

  const fetchApplicants = async (): Promise<void> => {
    isLoading.value = true
    error.value = false

    await systemStore.setLoading(true)

    const result = await api.get('/tiktok_applicants', { params: params.value })

    await systemStore.setLoading(false)
    firstLoaded.value = true
    isLoading.value = false

    if (!result.data) {
      error.value = true
      return
    }

    applicants.value = result.data.applicants

    if (params.value.pagingNo === 1) {
      total.value = result.data.dataCount
    }
  }

  const fetchEntryCount = async (fetchParams: DeepReadonly<TEntryCount>): Promise<void> => {
    error.value = false

    const result = await api.get('/tiktok_applicants/entry_counts', { params: fetchParams })

    if (!result.data) {
      error.value = true
      return
    }

    entryCounts.value = result.data.count
  }

  const fetchCampaign = async (): Promise<void> => {
    const result = await api.get('/campaigns/' + params.value.campaignId)
    if (result.data) {
      if (!result.data.account) {
        result.data.account = {
          id: null,
          name: '削除されたアカウント',
          pictureUrl: ''
        }
      }
      campaign.value = result.data
    }
  }

  const fetchCampaignPrize = async (): Promise<void> => {
    const result = await api.get('/tiktok_campaign_prizes/' + params.value.prizeId)
    if (result.data) {
      campaignPrize.value = result.data
    }
  }

  const updateNgAccount = async (payload: { screenName: string; isNg: boolean; ngApplicantId: number | null }) => {
    if (!campaign.value) return null
    if (!payload.isNg && !payload.ngApplicantId) return null

    const response = payload.isNg
      ? await api.post('/tiktok_ng_applicants', {
          groupId: campaign.value.groupId,
          screenNames: [payload.screenName]
        })
      : await api.delete(`/tiktok_ng_applicants/${payload.ngApplicantId}`)

    if (response.data) await fetchApplicants()
    return response
  }

  const setParams = (newParams: Partial<TGetApplicantsParams>): void => {
    params.value = {
      ...params.value,
      ...newParams,
      pagingNo: newParams.pagingNo || 1
    }
  }

  const toggleShowAdvanceFilter = (): void => {
    TrackingService.sendEvent('click:応募一覧（IG）|詳細検索を終了')
    showAdvanceFilter.value = !showAdvanceFilter.value
  }

  const updateWinnerContact = async (payload: TPutWinnerContactParams) => {
    const result = await api.put('/tiktok_applicants/' + payload.applicantId, {
      isWinnerContact: payload.isWinnerContact
    })

    if (result.data) {
      applicants.value.forEach((applicant, index) => {
        if (applicant.id === payload.applicantId) {
          applicant.isWinnerContact = payload.isWinnerContact
          if (
            (params.value.isWinnerContact === 0 && payload.isWinnerContact === 1) ||
            (params.value.isWinnerContact === 1 && payload.isWinnerContact === 0)
          ) {
            applicants.value.splice(index, 1)
            total.value--
          }
        }
      })

      if (campaignPrize.value && payload.isWinnerContact === 1) {
        campaignPrize.value.winnerContactCount++
      }

      if (campaignPrize.value && payload.isWinnerContact === 0) {
        campaignPrize.value.winnerContactCount--
      }
    }

    if (applicants.value.length === 0 && total.value > 0) {
      await fetchApplicants()
    }

    return result
  }

  const updateFollower = async (payload: TPutFollowerParams) => {
    const result = await api.put('/tiktok_applicants/' + payload.applicantId, {
      isFollower: payload.isFollowing
    })

    if (result.data) {
      applicants.value.forEach((applicant, index) => {
        if (applicant.id === payload.applicantId) {
          applicant.isFollowing = payload.isFollowing
          if (
            (params.value.isFollowing === 0 && payload.isFollowing === 1) ||
            (params.value.isFollowing === 1 && payload.isFollowing === 0)
          ) {
            applicants.value.splice(index, 1)
            total.value--
          }
        }
      })

      if (campaignPrize.value && payload.isFollowing === 1) {
        campaignPrize.value.followersCount++
      }

      if (campaignPrize.value && payload.isFollowing === 0) {
        campaignPrize.value.followersCount--
      }
    }

    if (applicants.value.length === 0 && total.value > 0) {
      await fetchApplicants()
    }

    return result
  }

  const updateCandidate = async (payload: TPutCandidateParams) => {
    const result = await api.put('/tiktok_applicants/' + payload.applicantId, {
      isCandidate: payload.isCandidate
    })

    if (result.data) {
      applicants.value.forEach((applicant, index) => {
        if (applicant.id === payload.applicantId) {
          applicant.isCandidate = payload.isCandidate
          if (
            (params.value.isCandidate === 0 && payload.isCandidate === 1) ||
            (params.value.isCandidate === 1 && payload.isCandidate === 0)
          ) {
            applicants.value.splice(index, 1)
            total.value--
          }
        }
      })

      if (campaignPrize.value && payload.isCandidate === 1) {
        campaignPrize.value.candidateCount++
      }

      if (campaignPrize.value && payload.isCandidate === 0) {
        campaignPrize.value.candidateCount--
      }
    }

    if (applicants.value.length === 0 && total.value > 0) {
      await fetchApplicants()
    }

    return result
  }

  const updateWinner = async (payload: TPutWinnerParams) => {
    if (!campaignPrize.value) {
      return
    }

    const result = await api.put('/tiktok_applicants/' + payload.applicantId, {
      prizeId: campaignPrize.value.id,
      isWinner: payload.isWinner,
      wasWinner: payload.wasWinner
    })

    if (result.data) {
      applicants.value.forEach((applicant, index) => {
        if (applicant.id === payload.applicantId) {
          applicant.isWinner = payload.isWinner

          if (
            (params.value.isWinner === 0 && payload.isWinner === 1) ||
            (params.value.isWinner === 1 && payload.isWinner === 0)
          ) {
            applicants.value.splice(index, 1)
            total.value--
          }
        }
      })

      if (campaignPrize.value && payload.isWinner === 1) {
        campaignPrize.value.winnerCount++
      }

      if (campaignPrize.value && payload.isWinner === 0) {
        campaignPrize.value.winnerCount--
      }
    }

    if (applicants.value.length === 0 && total.value > 0) {
      await fetchApplicants()
    }

    return result
  }

  const updateResponder = async (payload: TPutResponderParams) => {
    const result = await api.put('/tiktok_applicants/' + payload.applicantId, {
      isResponder: payload.isResponder
    })

    if (result.data) {
      applicants.value.forEach((applicant, index) => {
        if (applicant.id === payload.applicantId) {
          applicant.isResponder = payload.isResponder
          if (
            (params.value.isResponder === 0 && payload.isResponder === 1) ||
            (params.value.isResponder === 1 && payload.isResponder === 0)
          ) {
            applicants.value.splice(index, 1)
            total.value--
          }
        }
      })

      if (campaignPrize.value && payload.isResponder === 1) {
        campaignPrize.value.responderCount++
      }

      if (campaignPrize.value && payload.isResponder === 0) {
        campaignPrize.value.responderCount--
      }
    }

    if (applicants.value.length === 0 && total.value > 0) {
      await fetchApplicants()
    }

    return result
  }

  return {
    firstLoaded: readonly(firstLoaded),
    error: readonly(error),
    total: readonly(total),
    applicants: readonly(applicants),
    params: readonly(params),
    campaign: readonly(campaign),
    campaignPrize: readonly(campaignPrize),
    showAdvanceFilter: readonly(showAdvanceFilter),
    isLoading: readonly(isLoading),
    entryCounts: readonly(entryCounts),
    reset,
    fetchApplicants,
    fetchEntryCount,
    fetchCampaign,
    fetchCampaignPrize,
    updateNgAccount,
    setParams,
    toggleShowAdvanceFilter,
    updateWinnerContact,
    updateFollower,
    updateCandidate,
    updateWinner,
    updateResponder
  }
})
